import React from 'react';
import intl from 'react-intl-universal';
import classNames from 'classnames';
import {observer} from 'mobx-react-lite';

import {useAppState} from '@stores/AppStateContext';
import type {ItemModelType, ViewType, WorkspaceModelType} from '@stores/WorkspaceStore';
import {PAGES} from '@stores/ViewStore';

import {changeView} from '../../../../actions/ui/changeView';
import {ContextMenuWithNavigation, ContextMenuWithNavigationItem} from '../../../components/ContextMenuWithNavigation';
import {AddIcon} from '../../../components/icons/AddIcon';
import {ArchiveIcon} from '../../../components/icons/ArchiveIcon';
import {CheckIcon} from '../../../components/icons/CheckIcon';
import {DeleteIcon} from '../../../components/icons/DeleteIcon';
import {GroupIcon} from '../../../components/icons/GroupIcon';
import {HistoryIcon} from '../../../components/icons/HistoryIcon';
import {MoreVerticalIcon} from '../../../components/icons/MoreVerticalIcon';
import {Tooltip} from '../../../components/Tooltip';
import {toggleContextMenu} from '../toggleContextMenu';
import {MODALS} from '../../../screens/modals/modal-names';
import {ContextMenuInterface} from '../ContextMenuInterface';

import './TreeHeaderOptions.css';

export interface TreeHeaderOptionsProps extends ContextMenuInterface {
    libraries: ItemModelType[];
    selectedLibrary?: ItemModelType | null;
    workspace: WorkspaceModelType;
}

type TreeHeaderOptionMeta =
    | {
        type: 'library';
        library: ItemModelType;
    }
    | {
        type: 'view';
        view: ViewType;
    };

export const TreeHeaderOptions = observer((
    {
        handleClose,
        isDark,
        libraries,
        selectedLibrary,
        workspace,
    }: TreeHeaderOptionsProps
) => {
    const appState = useAppState();
    const rootStore = appState.rootStore;
    const viewOptions = [
        {
            value: 'recent' as ViewType,
            label: intl.get('workspace-row-context.recent').d('Recent'),
            Icon: HistoryIcon,
        },
        {
            value: 'active' as ViewType,
            label: intl.get('workspace-row-context.active').d('Active'),
            Icon: GroupIcon,
        },
        {
            value: 'archive' as ViewType,
            label: intl.get('workspace-row-context.archive').d('Archive'),
            Icon: ArchiveIcon,
        },
        {
            value: 'trash' as ViewType,
            label: intl.get('workspace-row-context.trash').d('Trash'),
            Icon: DeleteIcon,
        },
    ];

    const openCreateLibrary = (event: React.MouseEvent) => {
        event.stopPropagation();
        appState.setContextMenu(null);
        appState.setModal({name: MODALS.SECTION_CREATE, params: {workspace}});
    };

    const openLibraryOptions = (event: React.MouseEvent<HTMLElement>, library: ItemModelType) => {
        event.stopPropagation();
        const target = event.currentTarget;
        toggleContextMenu(appState, {
            id: `library-options-${library.id}`,
            type: 'library-row-options',
            shouldForcePlace: true,
            menuPlacement: 'block-end',
            menuAlignment: 'end',
            VERTICAL_SHIFT: 4,
            HORIZONTAL_SHIFT: 0,
            getTargetBoundingBox: target.getBoundingClientRect.bind(target),
            params: {library},
        }, {asAdditional: true, closeOthers: false});
    };

    const libraryItems: ContextMenuWithNavigationItem[] = libraries.map(library => {
        const label = library.name || intl.get('common.untitled_section').d('Untitled section');
        const isSelected = workspace.view === 'main' && selectedLibrary?.id === library.id;

        return {
            id: library.id,
            text: label,
            textForFilter: label,
            meta: {
                type: 'library',
                library,
            } as TreeHeaderOptionMeta,
            getContent: (_ctx, meta, innerRef) => (
                <div
                    ref={innerRef}
                    className={classNames({
                        'TreeHeaderOptions__row': true,
                        'TreeHeaderOptions__libraryRow': true,
                        'TreeHeaderOptions__row--focused': meta.isInNavigationFocus,
                    })}
                    onClick={meta.onClick}
                    role="menuitem"
                    tabIndex={-1}
                >
                    <span className="TreeHeaderOptions__text">
                        {label}
                    </span>
                    {isSelected && (
                        <CheckIcon
                            className="TreeHeaderOptions__check"
                            size={20}
                        />
                    )}
                    <Tooltip title={intl.get('library.options.tooltip').d('Library options')}>
                        <MoreVerticalIcon
                            className="TreeHeaderOptions__libraryOptions"
                            size={20}
                            additionalPadding={2}
                            onClick={(event) => openLibraryOptions(event as React.MouseEvent<HTMLElement>, library)}
                        />
                    </Tooltip>
                </div>
            ),
        };
    });

    const viewItems: ContextMenuWithNavigationItem[] = viewOptions.map(option => ({
        id: `view-${option.value}`,
        text: option.label,
        textForFilter: option.label,
        meta: {
            type: 'view',
            view: option.value,
        } as TreeHeaderOptionMeta,
        getContent: (_ctx, meta, innerRef) => {
            const Icon = option.Icon;

            return (
                <div
                    ref={innerRef}
                    className={classNames({
                        'TreeHeaderOptions__row': true,
                        'TreeHeaderOptions__viewRow': true,
                        'TreeHeaderOptions__row--focused': meta.isInNavigationFocus,
                        'TreeHeaderOptions__viewRow--active': workspace.view === option.value,
                    })}
                    onClick={meta.onClick}
                    role="menuitem"
                    tabIndex={-1}
                >
                    <Icon className="TreeHeaderOptions__viewIcon" size={20}/>
                    <span className="TreeHeaderOptions__viewText">
                        {option.label}
                    </span>
                </div>
            );
        },
    }));

    const itemGroups = [
        libraryItems,
        viewItems,
    ].filter(group => group.length > 0);

    const header = (
        <div className="TreeHeaderOptions__header">
            <span className="TreeHeaderOptions__headerText label-small-bold">
                {intl.get('TreeHeaderOptions.libraries').d('LIBRARIES')}
            </span>
            <Tooltip title={intl.get('TreeHeaderOptions.addLibrary').d('Create Library')}>
                <AddIcon
                    className="TreeHeaderOptions__headerIcon"
                    size={20}
                    onClick={openCreateLibrary}
                />
            </Tooltip>
        </div>
    );

    return (
        <ContextMenuWithNavigation
            headerNode={header}
            allowFilter={false}
            className="TreeHeaderOptions body-large"
            handleClose={handleClose}
            withBorder={false}
            itemGroups={itemGroups}
            isDark={isDark}
            onItemClick={(item) => {
                const option = item.meta as TreeHeaderOptionMeta;

                if (option.type === 'library') {
                    changeView(rootStore, 'main', workspace);
                    workspace.setSelectedLibraryId(option.library.id);
                    rootStore.view.openPageByName(PAGES.KNOWLEDGE_BASE, {
                        libraryId: option.library.id,
                        libraryName: option.library.name || undefined,
                    });
                    appState.setContextMenu(null);
                    return;
                }

                changeView(rootStore, option.view, workspace);
                appState.setContextMenu(null);
            }}
        />
    );
});
